import React, {
  useCallback,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction
} from "react"
import { useNavigate } from "react-router-dom"
import debounce from "lodash.debounce"
import {
  AccountCircleRounded,
  CloseRounded,
  Euro,
  AttachMoney,
  CurrencyYen,
  CurrencyPound
} from "@mui/icons-material"
import {
  Box,
  ButtonBase,
  Button,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Pagination,
  TextField,
  Typography,
  Select,
  FormControl,
  OutlinedInput,
  Modal
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import Navigation from "../components/navigation"
import ShopItemCard from "../components/shopItemCard"
import ShopItemModal from "../components/shopItemModal"
import { MultiplePropertiesArea } from "../components/propertyArea"
import { CustomSelect } from "../components/customSelect"
import { useSpareParts } from "../providers/spareParts"
import { useAuth } from "../providers/auth"
import {
  styles,
  stylesDark,
  Styles,
  useTheme
} from "../providers/theme"
import useWidth from "../hooks/window"

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

export default function SpareParts() {
  const navigate = useNavigate()
  const {mode} = useTheme()
  const width = useWidth()
  const {
    spareParts,
    currencyIcons,
    distributors,
    pagesCount,
    search,
    sortByItem,
    filterByModelAndVersion,
    displayMode,
    currentPage,
    currencies,
    companyName,
    itemsPerPage,
    searchResults,
    selectedItem,
    setSearch,
    setSortByItem,
    setFilterByModelAndVer,
    // setDisplayMode, // Commented out for the time being (grid view was not desired, we're using list view instead)
    setCurrentPage,
    setCurrencies,
    setCompanyName,
    setItemsPerPage,
  } = useSpareParts()

  const title = 'Spare parts shop'

  let classesLight = useStylesLight(styles)
  let classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark

  const {currentRole} = useAuth()
  const [localSearch, setLocalSearch] = useState<string>(search)
  const [modalContent, setModalContent] = useState<ReactNode>(<></>)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const itemsOnPageValues: number[] = [15, 30, 45]

  const searchSpareParts = (localSearch: string) => {
    setSearch(localSearch)
  }

  // eslint-disable-next-line
  const debouncedSearchSpareParts = useCallback(debounce(searchSpareParts, 300), [])

  useEffect(() => {
    debouncedSearchSpareParts(localSearch)

    return () => {
      debouncedSearchSpareParts.cancel()
    }
    // eslint-disable-next-line
  }, [localSearch])

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value
    setLocalSearch(newSearch)
  }

  const onChangeItemsOnPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItemsOnPage = parseInt(event.target.value)
    setItemsPerPage(newItemsOnPage)
  }

  return (
    <Navigation
      title={title}
      actions={
        <>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="account-appbar"
            aria-haspopup="true"
            color="inherit"
            edge="end"
            onClick={() => navigate('/account')}
          >
            <AccountCircleRounded />
          </IconButton>
        </>
      }
    >
      <Container sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: "100%",
            display: "flex"
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end"
            }}
          >
            <Typography variant="h4" component="h1" my={2}>
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              component="h2"
              ml={2}
              my={2}
              sx={{ color: `${mode === "light" ? "#25252580" : "#FFFFFF80"}` }}
            >
              {`${searchResults} ${searchResults === 1 ? "result" : "results"}`}
            </Typography>
          </Box>

          {/* Commented out for the time being (grid view was not desired, we're using list view instead)
            <Box
            sx={{
              width: "auto",
              display: "flex",
              alignItems: "center"
            }}
          >
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "list" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('list')
              }}
            >
              <ListRounded />
            </IconButton>
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "grid" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('grid')
              }}
            >
              <GridViewRounded />
            </IconButton>
          </Box> */}
        </Box>
        <div
            style={{
              display: "flex",
            }}
        >
          <TextField
            sx={{
              width: "80%"
            }}
            defaultValue={search ?? ""}
            label="Search"
            size="small"
            onChange={onChangeSearch}
          />
          <Button
            sx={{
              width: "10%",
              paddingInline: "2em !important",
              marginLeft: ".5em",
              border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
              borderRadius: "10px",
              color: `${mode === "light" ? "#252525" : "#FFF"}`,
              "&:hover": {
                border: `1px solid ${mode === "light" ? "#252525" : "#FFF"}`,
                backgroundColor: `${mode === "light" ? "#252525" : "#FFF"}`,
                color: `${mode === "light" ? "#FFF" : "#252525"}`
              }
            }}
            onClick={() => {
              setModalContent(
                <Box
                  sx={{
                    backgroundColor: mode === 'light' ? "#F6F6F6" : "#252525",
                    borderRadius: "10px",
                    boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)",
                    padding: "1em",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                  >
                    Sort By
                  </Typography>
                  <MultiplePropertiesArea
                    topMargin={true}
                    paddingInline={true}
                    property={"Item"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={false}
                        items={[
                          {
                            value: "Default",
                            selected: "default" === sortByItem.toLowerCase()
                          },
                          {
                            value: "Ascending",
                            selected: "ascending" === sortByItem.toLowerCase()
                          },
                          {
                            value: "Descending",
                            selected: "descending" === sortByItem.toLowerCase()
                          }
                        ]}
                        setFunction={setSortByItem as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                </Box>
              )
              setModalOpen(true)
            }}
          >
            Sort
          </Button>
        </div>
        {
          currentRole !== undefined && currentRole.includes('Administrator') &&
          <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginTop: "1em"
            }}
          >
            {
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  marginLeft: ".5em",
                  marginTop: ".1em",
                  whiteSpace: "nowrap"
                }}
              >
                Select company:
              </Typography>
            }
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                width: "100%",
                height: "auto"
              }}
            >
              <FormControl sx={{ width: "30%", ml: 2, mt: 0, mb: 0 }}>
                <Select
                  sx={{
                    height: "2em"
                  }}
                  multiple={false}
                  value={companyName}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return "None"
                    }
                    return selected;
                  }}
                  onChange={(event) => {
                    const { target: { value } } = event
                    if (value !== companyName) {
                      setCompanyName(value);
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: mode === 'light' ? "#fff" : "#151515",
                        borderRadius: '10px',
                        '&::-webkit-scrollbar': {
                          width: ".3em",
                        },
                        '&::-webkit-scrollbar-track': {
                          background: "transparent",
                          boxShadow: "inset 0 0 10px #1c1c1c",
                          borderRadius: "5px",
                          borderLeft: "1px solid transparent",
                          borderRight: "1px solid transparent",
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#444',
                          outline: '1px solid #444',
                          borderRadius: ".15em"
                        }
                      }
                    }
                  }}
                  input={<OutlinedInput />}
                >
                  {
                    distributors.map((item: string) => (
                          <MenuItem
                            key={item}
                            value={item}
                            onClick={() => {
                              // Deselect
                              if (companyName === item) {
                                setCompanyName("");
                              }
                            }}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "rgba(133, 210, 1, 0.3)",
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "rgba(133, 210, 1, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                              }
                            }}
                          >
                            {item}
                          </MenuItem>
                        ))
                  }
                </Select>
              </FormControl>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginTop: "1em"
            }}
          >
            {
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  marginLeft: ".5em",
                  marginTop: ".25em"
                }}
              >
                Currency:
              </Typography>
            }
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                width: "100%",
                height: "auto"
              }}
            >
              <Box sx={{ display: "flex", gap: "0.5em", marginLeft: "1em" }}>
                {[
                  { value: "EUR", Icon: Euro },
                  { value: "USD", Icon: AttachMoney },
                  { value: "JPY", Icon: CurrencyYen },
                  { value: "GBP", Icon: CurrencyPound },
                  { value: "SEK", Icon: () => <Box sx={{ fontWeight: 600 }}>SEK</Box> }
                ].map(({ value, Icon }, index) => (
                  <Box
                    key={`${value}-${index}`}
                    onClick={() => setCurrencies(value)}
                    sx={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: (() =>  {
                        if (currencies === value) {
                          return mode === "dark" ? "#ffffff" : "#d6d6d6";
                        }
                      })(),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: (() => {
                        if (currencies === value && mode === "dark") {
                          return "#252525";
                        }
                      })(),
                      cursor: "pointer",
                      transition: "background-color .2s",
                      "&:hover": {
                        backgroundColor: currencies === value ? "#d6d6d6" : "#9e9e9e"
                      }
                    }}
                  >
                    <Icon fontSize="small" sx={{ marginRight: "1px"}} />
                  </Box>
                ))}
              </Box>
            </div>
          </div>
          </>
        }
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginTop: "1em"
          }}
        >
          {
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{
                marginLeft: ".5em",
                marginTop: ".05em"
              }}
            >
              Model:
            </Typography>
          }
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              height: "auto"
            }}
          >
            <Box sx={{ display: "flex", gap: "0.5em", marginLeft: "1.5em" }}>
              {[
                { value: "EV", Icon: () => <Box sx={{ fontWeight: 600 }}>Everest-D</Box> },
                { value: "BA", Icon: () => <Box sx={{ fontWeight: 600 }}>Basic-D</Box> },
                { value: "BB", Icon: () => <Box sx={{ fontWeight: 600 }}>BrailleBox</Box> },
                { value: "FF", Icon: () => <Box sx={{ fontWeight: 600 }}>Fanfold-D</Box> },
                { value: "FF+", Icon: () => <Box sx={{ fontWeight: 600 }}>Fanfold+</Box> },
                { value: "", Icon: (props: any) => (<Box {...props} sx={{ fontWeight: 600 }}>Clear All</Box>)},
              ].map(({ value, Icon }) => (
              <ButtonBase key={value}
                sx={{
                  '& .MuiTouchRipple-root': {
                    borderRadius: "5em"
                  }
                }}
              >
                <Box
                  onClick={() =>
                    setFilterByModelAndVer((prev: string) => {
                      if (value === "") return "";
                      // Convert string to array.
                      const parts = prev ? prev.split(";") : [];

                      // If the value exists, remove/deselect it.
                      if (parts.includes(value)) {
                        const filtered = parts.filter(item => item !== value);
                        // Convert back to string
                        return filtered.join(";");
                      }

                      // If the value does not exist, add it.
                      return prev ? `${prev};${value}` : value;
                    })
                  }
                  sx={{
                    width: "100px",
                    height: "30px",
                    borderRadius: "5em",
                    backgroundColor: (() =>  {
                      if (filterByModelAndVersion.split(";").includes(value) && value !== "") {
                        return mode === "dark" ? "#ffffff" : "#d6d6d6";
                      }
                    })(),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: (() => {
                      if (filterByModelAndVersion.split(";").includes(value) && value !== "" && mode === "dark") {
                        return "#252525";
                      }
                    })(),
                    cursor: "pointer",
                    transition: "background-color .2s",
                    "&:hover": {
                      backgroundColor: currencies === value ? "#d6d6d6" : "#9e9e9e"
                    }
                  }}
                >
                  <Icon sx={{ marginRight: "1px"}} />
                </Box>
              </ButtonBase>
              ))}
            </Box>
          </div>
        </div>
        {
          displayMode === 'grid' ?
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "center",
                mt: 2,
                mb: 2
              }}
            >
              {
                spareParts.length > 0 ?
                  spareParts.map((sparePart, index) =>
                    <Grid
                      item
                      key={`${sparePart}-${index}`}
                    >
                      <ShopItemCard
                        sparePart={sparePart}
                        displayMode={displayMode}
                        currencyIcons={currencyIcons}
                      />
                    </Grid>
                  )
                :
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h2"
                  >
                    No match
                  </Typography>
              }
            </Grid>
          :
            spareParts.length > 0 ?
              <>
                <Box
                  sx={{
                    width: "100%",
                    display: "grid",
                    paddingInline: "2em",
                    paddingLeft: "clamp(95px, 8%, 50px)",
                    gridTemplateColumns: "minmax(59px, 6.5%) minmax(125px, 42%) minmax(80px, 19%) minmax(80px, 20%) 0px 0px",
                    marginTop: "3em",
                    gap: ".5em"
                  }}
                >
                  <p
                    style={{
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Item
                  </p>
                  <p
                    style={{
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Description
                  </p>
                  <p
                    style={{
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Price
                  </p>
                  <p
                    style={{
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Discounted price
                  </p>
                  <Button
                    variant="contained"
                    sx={{
                      maxWidth: "100px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                    Open
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      maxWidth: "100px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                    Cart
                  </Button>
                </Box>
                <List
                  sx={{
                    width: "100%",
                    height: "auto"
                  }}
                >
                  {
                    spareParts.length > 0 &&
                      spareParts.map((sparePart, index) =>
                        <ListItem
                          key={`${sparePart}-${index}`}
                          disableGutters
                        >
                          <ShopItemCard
                            sparePart={sparePart}
                            displayMode={displayMode}
                            currencyIcons={currencyIcons}
                          />
                        </ListItem>
                      )
                  }
                </List>
              </>
            :
              <Typography
                gutterBottom
                variant="h5"
                component="h2"
              >
                No match
              </Typography>
          }
          {
            pagesCount !== 0 &&
              <Box
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "flex",
                  flexDirection: `${width < 1000 ? "column" : "row"}`,
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBlock: "1em 2.5em"
                }}
              >
                <TextField
                  sx={{
                    width: "150px",
                    position: "relative"
                  }}
                  fullWidth
                  value={itemsPerPage}
                  label="Items on page"
                  size="small"
                  select
                  onChange={onChangeItemsOnPage}
                >
                  {
                    itemsOnPageValues.map((itemsOnPageValue: number, index: number) =>
                      <MenuItem
                        key={index}
                        value={itemsOnPageValue}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "rgba(133, 210, 1, 0.3)",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "rgba(133, 210, 1, 0.3)",
                          },
                          "&:hover": {
                            backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                          }
                        }}
                      >
                        {itemsOnPageValue}
                      </MenuItem>
                    )
                  }
                </TextField>
                <Pagination
                  sx={{
                    marginBlock: '1em',
                    position: "relative",
                    right: "50%",
                    display: "flex",
                    width: `${width < 1000 ? "100%" : "auto"}`,
                    justifyContent: "center",
                    transform: "translateX(50%)"
                  }}
                  count={pagesCount}
                  page={currentPage}
                  onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: 'smooth'
                    })
                    setCurrentPage(newPage)
                  }}
                  size="large"
                />
              </Box>
          }
          {
            modalOpen &&
              <Modal
                open={modalOpen}
                onClose={() => {
                  setModalOpen(false)
                }}
              >
                <Box
                  className={classes.modalContainer}
                  sx={{
                    width: "50%",
                    minWidth: "300px",
                    maxWidth: "700px",
                    paddingTop: "1.5em !important",
                    paddingBottom: "1em"
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "calc(100% - 1em)",
                      overflowY: "auto",
                      marginTop: "1em",
                      borderRadius: "10px",
                      marginRight: "1.5em",
                      padding: "1em"
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "auto",
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      {modalContent}
                    </Box>
                  </Box>
                  <IconButton
                    className={classes.modalButtonClose}
                    onClick={() => {
                      setModalOpen(false)
                    }}
                  >
                    <CloseRounded />
                  </IconButton>
                </Box>
              </Modal>
          }
          {
            selectedItem &&
              <ShopItemModal
                sparePart={selectedItem}
                currencyIcons={currencyIcons}
              />
          }
        </Container>
    </Navigation>
  )
}
